import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import RadioGroup, { Option } from 'shared/components/radio-group'
import { translateServerSideProps } from 'shared/utils/translate-server-side-props'
import ResubscribedCard from 'modules/unsubscribe-page/components/resubscribed-card'
import UnsubscribeCard from 'modules/unsubscribe-page/components/unsubscribed-card'
import { UnsubscribeTypeEnum } from 'modules/unsubscribe-page/enum/unsubscribe-type-enum'
import { useDisplayUnsubscribeFromCampaign } from 'modules/unsubscribe-page/hooks/use-display-unsubscribe-from-campaign'
import { useResubscribe } from 'modules/unsubscribe-page/hooks/use-resubscribe'
import { useUnsubscribe } from 'modules/unsubscribe-page/hooks/use-unsubscribe'
import { getUnsubscribePageLayout } from 'modules/unsubscribe-page/layouts/get-unsubscribe-page-layout'

function Unsubscribe() {
  const { t } = useTranslation()

  const { query } = useRouter()
  const path = query.path as string[]
  const slug = path[2] as string
  const messageId = path[3] as string

  const [unsubscribeType, setUnsubscribeType] = useState(
    UnsubscribeTypeEnum.unsubscribe_all_marketing_emails,
  )
  const { data } = useDisplayUnsubscribeFromCampaign(messageId)
  const [isSuccess, setIsSuccess] = useState(false)

  const {
    unsubscribeFromAllMarketingEmails,
    unsubscribeFromCampaign,
    isFetching: isFetchingUnsubscribe,
  } = useUnsubscribe()
  const {
    resubscribeToMarketingEmails,
    resubscribeToMarketingEmailsCampaign,
    isFetching: isFetchingResubscribe,
  } = useResubscribe()

  const [options, setOptions] = useState<Option<UnsubscribeTypeEnum>[]>([])

  async function confirmResubscribed() {
    if (unsubscribeType === UnsubscribeTypeEnum.unsubscribe_campaign) {
      await resubscribeToMarketingEmailsCampaign(slug, messageId, String(data?.campaign.id))
    } else {
      await resubscribeToMarketingEmails(slug, messageId)
    }
  }

  async function confirmUnsubscribe() {
    try {
      if (unsubscribeType === UnsubscribeTypeEnum.unsubscribe_campaign) {
        await unsubscribeFromCampaign(slug, messageId, String(data?.campaign.id))
      } else {
        await unsubscribeFromAllMarketingEmails(slug, messageId)
      }
      setIsSuccess(true)
    } catch (e) {}
  }

  useEffect(() => {
    if (data) {
      setOptions(() => [
        {
          value: UnsubscribeTypeEnum.unsubscribe_all_marketing_emails,
          label: t('unsubscribe_page.unsubscribe.from_all_marketing_emails'),
        },
      ])
      if (data.displayUnsubscribeFromCampaignLink)
        setOptions(prev => [
          {
            value: UnsubscribeTypeEnum.unsubscribe_campaign,
            label: t('unsubscribe_page.unsubscribe.from_campaign'),
          },
          ...prev,
        ])
    }
  }, [data, t])

  return (
    <div className="flex flex-col items-center justify-center">
      {isSuccess ? (
        <ResubscribedCard
          title={t('mailing.unsubscribe_page.unsubscribed_success.title')}
          subTitle={
            unsubscribeType === UnsubscribeTypeEnum.unsubscribe_campaign
              ? t('mailing.unsubscribed_page.unsubscribed.campaign.text')
              : t('mailing.unsubscribed_page.unsubscribed.marketing_emails.text')
          }
          isFetching={isFetchingResubscribe}
          onConfirmResubscribed={confirmResubscribed}
        />
      ) : (
        <>
          {data ? (
            <UnsubscribeCard
              isSuccess={true}
              isFetching={isFetchingUnsubscribe}
              onConfirmUnsubscribe={confirmUnsubscribe}
              title={t('unsubscribe_page.unsubscribe.title')}
            >
              <RadioGroup
                containerWrapperClassName={'w-full items-start mb-4'}
                optionsWrapperClassName={'flex-col mb-5'}
                selected={unsubscribeType}
                options={options}
                onChange={value => setUnsubscribeType(value)}
                disabled={isFetchingUnsubscribe}
              />
            </UnsubscribeCard>
          ) : (
            <UnsubscribeSkeleton />
          )}
        </>
      )}
    </div>
  )
}

function UnsubscribeSkeleton() {
  return (
    <>
      <div className="mb-3 size-[149px] rounded-full bg-skeleton-primary" />
      <div className="w-full">
        <div className="mb-8 h-[55px] w-full rounded-sm bg-skeleton-primary" />
        <div className="mb-4 flex items-center gap-2">
          <div className="size-6 rounded-full bg-skeleton-primary" />
          <div className="h-6 w-3/4 rounded-sm bg-skeleton-primary" />
        </div>
        <div className="mb-8 flex items-center gap-2">
          <div className="size-6 rounded-full bg-skeleton-primary" />
          <div className="h-6 w-3/4 rounded-sm bg-skeleton-primary" />
        </div>
        <div className="h-8 w-full rounded-sm bg-skeleton-primary" />
      </div>
    </>
  )
}

export const getServerSideProps = translateServerSideProps

Unsubscribe.getLayout = getUnsubscribePageLayout

export default Unsubscribe
